import React from 'react';
import PropTypes from 'prop-types';

import { Icon, Form, Select, Button, Message, Status, InfoText } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import { ReactComponent as Seat } from '@weezevent/throne/dist/images/seating/siege-available.svg';
import Style from './seatMapPopup.module.css';

import SeatMapPopOver from './seatMapPopOver';
import { getAlphaIndex } from '../utils';

function SelectAttendeeitem({ attendee, index, isAlreadySelected = false }) {
    const { t } = useTranslation();

    return (
        <span className={isAlreadySelected ? Style['item-selected'] : ''}>
            {t('ticket.swapSeat.popup.labelSelectItem', { number: index + 1, name: attendee.resolved_attendee_name })}
        </span>
    );
}

SelectAttendeeitem.propTypes = {
    attendee: PropTypes.array.isRequired,
    index: PropTypes.number.isRequired,
    isAlreadySelected: PropTypes.bool
};

function SelectAttendee({ seat = {}, attendees = [], newSeats = {}, onChange = () => {}, errors = [] }) {
    const { t } = useTranslation();

    const optionsAttendees = React.useMemo(() => {
        let options = [];
        if (!seat) {
            return options;
        }

        //Filter with verify compatibility contingent and category
        attendees.forEach(({ attendee, index }) => {
            if (attendee.isMatchingSeat(seat)) {
                let isAlreadySelected = Object.values(newSeats).some(({ attendeeId }) => attendeeId === attendee.pk);
                options.push({
                    text: <SelectAttendeeitem attendee={attendee} index={index} isAlreadySelected={isAlreadySelected} />,
                    value: attendee.id,
                    key: attendee.id
                });
            }
        });

        return options;
    }, [attendees, seat, newSeats]);

    return (
        <>
            {optionsAttendees.length ? (
                <Select
                    disabled={!optionsAttendees.length}
                    placeholder={t('ticket.swapSeat.popup.placeholderSelect')}
                    style={{ width: '100%' }}
                    errors={errors.includes(newSeats[seat.uuid]?.attendeeId)}
                    value={newSeats[seat.uuid]?.attendeeId || ''}
                    options={optionsAttendees}
                    onChange={(synth, data) => onChange(data.value, seat)}
                />
            ) : (
                <Message type="warning" title={t('ticket.swapSeat.popup.warningUnvailableSeat')} mini />
            )}
        </>
    );
}

SelectAttendee.propTypes = {
    seat: PropTypes.object,
    attendees: PropTypes.array,
    newSeats: PropTypes.object,
    onChange: PropTypes.func,
    errors: PropTypes.array
};

function LabelSeat({ seat = {}, index = 0 }) {
    const { t } = useTranslation();

    if (!seat) {
        return null;
    }

    return (
        <div>
            <div className={Style['seat-index']}>{getAlphaIndex(index)}</div>
            <div className={Style['label-seat']}>
                <style>
                    {`
                        svg.seat-category-edit {
                            width: 25px;
                            height: 23px;
                            margin-right: 3px;
                        }

                        svg.seat-category-edit-${seat.style.color.substring(1, seat.style.color.length)} path {
                            fill: ${seat.style.color};
                        }
                    `}
                </style>
                <div>
                    <Seat className={`seat-category-edit ${'seat-category-edit-' + seat.style.color.substring(1, seat.style.color.length)}`} />
                </div>
                <InfoText lineBreak className={Style['infotext-label']}>
                    {t('ticket.common.seating.labelInfosSeating', { seat: seat.seat, row: seat.row, access: seat.access })}
                </InfoText>
            </div>
        </div>
    );
}

LabelSeat.propTypes = {
    seat: PropTypes.object,
    index: PropTypes.number
};

function SeatSelectedOccupiedContent({ seat = {}, onClick = () => {}, onClose = () => {} }) {
    const { t } = useTranslation();

    return (
        <SeatMapPopOver
            className={Style['popup-occupied-seat__container']}
            title={
                <div className={Style['title__container']}>
                    <span>{t('ticket.common.seating.labelInfosSeating', { seat: seat.seat.seat, row: seat.seat.row, access: seat.seat.access })}</span>
                    <Icon className={Style['cross']} name="cross" onClick={() => onClose(false)} />
                </div>
            }
        >
            <div className={Style['popup__content']}>
                <style>
                    {`
                        svg.seat-category-over {
                            width: 16px;
                            height: 15px;
                            margin-right: 3px;
                        }

                        svg.seat-category-over-${seat.seat.style.color.substring(1, seat.seat.style.color.length)} path {
                            fill: ${seat.seat.style.color};
                        }
                    `}
                </style>
                <div className={Style['icon-seat']}>
                    <Seat className={`seat-category-over ${'seat-category-over-' + seat.seat.style.color.substring(1, seat.seat.style.color.length)}`} />
                </div>
                <span>{t('ticket.swapSeat.popup.seatOccupied', { name: seat.attendee.attendee.resolved_attendee_name })}</span>
            </div>
            <Button
                fluid
                inverted
                primary
                onClick={() => {
                    onClick(seat.attendee);
                }}
                label={t('ticket.swapSeat.popup.ctaChangeSeat')}
            />
        </SeatMapPopOver>
    );
}

SeatSelectedOccupiedContent.propTypes = {
    seat: PropTypes.object,
    onClick: PropTypes.func,
    onClose: PropTypes.func
};

function SeatsSelectedContent({ newSeats = {}, seatsSelected = [], attendees = [], errors = [], onChange = () => {}, onSubmit = () => {}, onClose = () => {} }) {
    const { t } = useTranslation();

    const labelAttendeeError = React.useMemo(() => {
        let attendee = attendees.filter(({ attendee }) => errors.includes(attendee.id))[0];
        if (!attendee) {
            return null;
        }
        return `${attendee.index + 1} - ${attendee.attendee.resolved_attendee_name}`;
    }, [attendees, errors]);

    return (
        <>
            <SeatMapPopOver
                title={
                    <div className={Style['title__container']}>
                        <span>{t('ticket.swapSeat.popup.title', { count: seatsSelected.length })}</span>
                        <Icon className={Style['cross']} name="cross" onClick={() => onClose(false)} />
                    </div>
                }
            >
                {Boolean(errors.length) && Object.values(newSeats).length > 0 && (
                    <Message
                        type="danger"
                        title={
                            errors.length === 1
                                ? t('ticket.swapSeat.popup.errorAttendee', {
                                      name: labelAttendeeError
                                  })
                                : t('ticket.swapSeat.popup.errorAttendees', { count: errors.length })
                        }
                        mini
                    />
                )}
                <div>
                    {seatsSelected.map((seat, index) => (
                        <Form.Field className={Style['field']} key={index}>
                            <Status
                                className={Style['status']}
                                text={seat.booking_state.toUpperCase() === 'AVAILABLE' ? t('ticket.common.seating.available') : t('ticket.common.seating.unavailable')}
                                color={seat.booking_state.toUpperCase() === 'AVAILABLE' ? 'green' : 'orange'}
                            />
                            <Form.Label label={<LabelSeat seat={seat} index={index} />} />
                            <SelectAttendee seat={seat} attendees={attendees} newSeats={newSeats} errors={errors} onChange={onChange} />
                        </Form.Field>
                    ))}
                </div>
            </SeatMapPopOver>
            <div className={Style['button__container']}>
                <Button fluid primary onClick={onSubmit} disabled={Object.values(newSeats).length === 0 || Boolean(errors.length)} label={t('ticket.common.action.apply')} />
            </div>
        </>
    );
}

SeatsSelectedContent.propTypes = {
    attendees: PropTypes.array,
    seatsSelected: PropTypes.array,
    newSeats: PropTypes.object,
    onChange: PropTypes.func,
    onSubmit: PropTypes.func,
    onClose: PropTypes.func,
    errors: PropTypes.array
};

export default function SeatMapPopup({ seatsSelected = [], onSeatsSelected = () => {}, attendees = [], onChangeAttendees = () => {}, handleAttendeeList = () => {} }) {
    const [newSeats, setNewSeats] = React.useState({});
    const [errors, setErrors] = React.useState([]);

    //"occupiedSeat" if a seat is occupied by a attendee who is in the props "attendees",
    //this allows to make a different render of the content of the popup
    const occupiedSeat = React.useMemo(() => {
        if (seatsSelected.length !== 1) {
            return;
        }
        let attendee =
            attendees.find(a => {
                if (a.new_seat?.uuid === seatsSelected[0].uuid) {
                    return a;
                }

                if (!a.new_seat && a.attendee.booking.seat_uuid === seatsSelected[0].uuid) {
                    return a;
                }

                return null;
            }) || null;
        return attendee ? { seat: seatsSelected[0], attendee: attendee } : null;
    }, [attendees, seatsSelected]);

    //Param setOnly for just update seatsSelected and not refresh map
    const onClose = React.useCallback(
        (setOnly = false) => {
            onSeatsSelected([], setOnly);
            setNewSeats({});
        },
        [onSeatsSelected]
    );

    const checkErrors = React.useCallback(newSeats => {
        setErrors(currentState => {
            let errorsTmp = [];
            let attendeeIds = [];
            Object.values(newSeats).forEach(({ attendeeId }) => {
                if (attendeeIds.includes(attendeeId)) {
                    errorsTmp.push(attendeeId);
                } else {
                    attendeeIds.push(attendeeId);
                }
            });
            return [...new Set(errorsTmp)];
        });
    }, []);

    const onChange = React.useCallback((attendeeId, seat) => {
        setNewSeats(currentState => {
            const newSeatsTmp = { ...currentState };
            newSeatsTmp[seat.uuid] = { seat, attendeeId };
            checkErrors(newSeatsTmp);
            return newSeatsTmp;
        });
    }, []);

    const swapOccupiedSeat = React.useCallback(
        attendee => {
            handleAttendeeList(attendee); //add attendee in attendeeSelected and refresh map
            onClose(true); //just reset seatsSelected for close popup
        },
        [handleAttendeeList, onClose]
    );

    const onSubmit = React.useCallback(() => {
        let attendeesTmp = [...attendees];
        onChangeAttendees(
            attendeesTmp.map(attendee => {
                Object.values(newSeats).forEach(({ seat, attendeeId }) => {
                    if (attendee.attendee.id === attendeeId) {
                        attendee.new_seat = seat;
                    }
                });

                return attendee;
            })
        );
        onSeatsSelected([]);
        setNewSeats({});
    }, [attendees, newSeats, onSeatsSelected, onChangeAttendees]);

    if (!seatsSelected.length) {
        return null;
    }

    let { left, top, width } = document.querySelector('.wz-split-container__content').getBoundingClientRect();

    return (
        <div
            className={Style['seat-map-popup']}
            style={{
                position: 'fixed',
                top: top + 35,
                left: left + width / 2 - 165,
                zIndex: 1
            }}
        >
            {occupiedSeat ? (
                <SeatSelectedOccupiedContent seat={occupiedSeat} onClick={swapOccupiedSeat} onClose={onClose} />
            ) : (
                <SeatsSelectedContent
                    newSeats={newSeats}
                    seatsSelected={seatsSelected}
                    attendees={attendees}
                    errors={errors}
                    onChange={onChange}
                    onSubmit={onSubmit}
                    onClose={onClose}
                />
            )}
        </div>
    );
}

SeatMapPopup.propTypes = {
    seatsSelected: PropTypes.array,
    onSeatsSelected: PropTypes.func,
    attendees: PropTypes.array,
    onChangeAttendees: PropTypes.func,
    handleAttendeeList: PropTypes.func
};
